package com.rosario.juridico

import androidx.compose.animation.*
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.automirrored.filled.Login
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.navigation.NavController
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import com.rosario.juridico.ui.screens.PantallaInfo
import com.rosario.juridico.navigation.JuridicoNavGraph
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.platform.AnalyticsLogger
import com.rosario.juridico.domain.*
import com.rosario.juridico.premium.PremiumManager
import com.rosario.juridico.ui.CommonBackHandler
import com.rosario.juridico.ui.screens.CalendarioParser

import com.rosario.juridico.ui.screens.PromoCodeDialog
import com.rosario.juridico.ui.components.DebugOverlay
import com.rosario.juridico.ui.theme.JuridicoTheme
import com.rosario.juridico.platform.UserStatus
import com.rosario.juridico.repositories.PreferencesRepository
import com.rosario.juridico.getPlatform
import com.rosario.juridico.openUrl
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.WorkspacePremium
import androidx.compose.material.icons.filled.Menu
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.material.icons.filled.AccountCircle
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.Info
import androidx.compose.material.icons.filled.Balance
import androidx.compose.material.icons.filled.Web
import androidx.compose.material.icons.filled.Shop
import androidx.compose.material.icons.filled.Share
import androidx.compose.material.icons.filled.ExpandLess
import androidx.compose.material.icons.filled.ExpandMore
import androidx.compose.material.icons.filled.Calculate
import androidx.compose.material.icons.filled.Handshake
import androidx.compose.material.icons.filled.AccountBalance
import androidx.compose.material.icons.filled.CalendarMonth
import androidx.compose.material.icons.filled.Alarm
import androidx.compose.material.icons.filled.Payments
import androidx.compose.material.icons.filled.Construction
import androidx.compose.material.icons.filled.Timeline
import androidx.compose.material.icons.filled.HomeWork
import androidx.compose.material.icons.filled.AutoAwesome
import androidx.compose.material.icons.filled.Apartment
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.automirrored.filled.TrendingUp
import androidx.compose.material.icons.filled.TrendingUp
import androidx.compose.material.icons.filled.HealthAndSafety
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.platform.ShareManager

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun JuridicoApp() {
    ShareManager.register()
    val darkTheme = isSystemInDarkTheme()
    val updateStatus by AppUpdateState.status.collectAsState()
    val downloadProgress by AppUpdateState.downloadProgress.collectAsState()
    val userStatus by AppUsageManager.instance.userStatus.collectAsState()
    var updateBannerDismissed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    
    // Configuración de persistencia y versión actual
    val CURRENT_APP_VERSION = 214 // Siguiendo el versionCode del build.gradle.kts
    val prefsRepository = remember { PreferencesRepository() }
    val lastVersionSeen by prefsRepository.lastVersionSeen.collectAsState()
    
    // ── ViewModel Unificado de Marketplace y Autenticación ────────────────
    val marketplaceViewModel = remember { MarketplaceViewModel(com.rosario.juridico.marketplace.billing.getBillingProvider()) }
    val isAuthInitializing by marketplaceViewModel.isAuthInitializing.collectAsState()
    val appInitState by marketplaceViewModel.appInitState.collectAsState()

    // ── Fase de Inicialización y Portero ──────────────────────────────
    // 1. LOADING: Esperando a que Firebase resuelva la sesión persistente.
    // 2. READY: Firebase ya respondió (null o usuario).
    var isAuthResolved by remember { mutableStateOf(false) }
    val authState by FirebaseProxy.observeAuthState().collectAsState(initial = null)
    val userEmail by AppUsageManager.instance.currentUserEmail.collectAsState()

    // Inicialización asíncrona de servicios y recuperación de identidad
    LaunchedEffect(Unit) {
        scope.launch {
            try {
                // 1. Sincronización inicial con Firebase Auth
                delay(800) 
                
                var currentUid = FirebaseProxy.getCurrentUserId()
                
                // 2. Si no hay UID, el sistema disparará signInAnonymously automáticamente 
                // dentro de observeUserStatus, así que aquí solo nos aseguramos de arrancar
                // los observers globales.
                AppUsageManager.instance.startObserving(currentUid ?: "")
                
                if (currentUid != null) {
                    FirebaseProxy.reloadUser()
                    PremiumManager.loadUserProfile(currentUid, forceRefresh = false)
                }
                
                // 3. BLOQUEO CRÍTICO: El Splash no se va hasta que el estado esté inicializado
                // con datos reales de la nube (o anónimo confirmado).
                AppUsageManager.instance.userStatus.first { it.isInitialized }
                
                // Precarga de datos secundarios
                launch { CalendarioParser.cargarTodo() }
                launch { obtenerDatosJus() }
                launch { cargarFeriados() }

            } catch (e: Exception) {
                println("AppDebug: Error en fase de inicio: ${e.message}")
            } finally {
                isAuthResolved = true
            }
        }
    }

    LaunchedEffect(updateStatus) {
        if (updateStatus == UpdateStatus.IDLE) {
            updateBannerDismissed = false
        }
    }
    
    JuridicoTheme(darkTheme = darkTheme) {
        // ── Portero de Tres Estados y Bloqueo de Carga ───────────────
        if (!isAuthResolved || isAuthInitializing || appInitState is com.rosario.juridico.marketplace.viewmodel.InitState.Loading) {
            SplashScreenNeutro()
        } else {
            val verificationStatus by AppUsageManager.instance.verificationStatus.collectAsState()
            
            val navController       = rememberNavController()
            val navBackStackEntry   by navController.currentBackStackEntryAsState()
            val currentRoute        = navBackStackEntry?.destination?.route
            
            // ── Navegación Automática por Estado de Auth ──────────
            LaunchedEffect(userEmail) {
                if (isAuthResolved && userEmail != null && currentRoute == Routes.LOGIN) {
                    // Si el usuario se autentica exitosamente (Google o Email) 
                    // y ya tiene el email verificado, lo llevamos al Inicio automáticamente.
                    if (FirebaseProxy.isEmailVerified()) {
                        navController.navigate(Routes.INICIO) {
                            popUpTo(Routes.LOGIN) { inclusive = true }
                        }
                    }
                }
            }

                val snackbarHostState   = remember { SnackbarHostState() }
                
                // Escuchar notificaciones de uso (UX: Alertas de 2 y 1 uso restante)
                LaunchedEffect(Unit) {
                    AppUsageManager.instance.usageNotifications.collect { message ->
                        snackbarHostState.showSnackbar(
                            message = message,
                            duration = SnackbarDuration.Long
                        )
                    }
                }

                var showLogoutDialog    by remember { mutableStateOf(false) }
                var showPremiumDialog   by remember { mutableStateOf(false) }
                var showPromoDialog     by remember { mutableStateOf(false) }
                var showNovedades       by remember { mutableStateOf(lastVersionSeen < CURRENT_APP_VERSION) }
                val drawerState         = rememberDrawerState(initialValue = DrawerValue.Closed)

                // Actualizar estado de visualización si cambia la persistencia
                LaunchedEffect(lastVersionSeen) {
                    if (lastVersionSeen < CURRENT_APP_VERSION) {
                        showNovedades = true
                    }
                }

                // Lógica de Destino: Si no hay sesión, permitimos Dashboard Público.
                // Ya no forzamos LOGIN como barrera total al iniciar.
                val startDestination = if (authState == null) Routes.INICIO else Routes.INICIO

                // Detección de retorno de Mercado Pago (Platform-Agnostic)
                LaunchedEffect(Unit) {
                    FirebaseProxy.handlePaymentReturn {
                        navController.navigate(Routes.PAYMENT_SUCCESS)
                    }
                }

                // Manejador del Botón Atrás
                CommonBackHandler(enabled = true) {
                    if (drawerState.isOpen) {
                        scope.launch { drawerState.close() }
                    } else if (navController.previousBackStackEntry != null) {
                        navController.popBackStack()
                    } else {
                        // En la raíz: minimizamos la aplicación sin cerrar sesión
                        exitApp()
                    }
                }

                val mostrarTopBar = currentRoute != null && 
                                   currentRoute != Routes.INICIO && 
                                   currentRoute != "inicio" &&
                                   !currentRoute.contains("login")

                ModalNavigationDrawer(
                    drawerState = drawerState,
                    gesturesEnabled = true,
                    drawerContent = {
                        ModalDrawerSheet {
                            val drawerScrollState = rememberScrollState()
                            WebScrollableBox(scrollState = drawerScrollState, modifier = Modifier.fillMaxHeight()) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxHeight()
                                        .verticalScroll(drawerScrollState)
                                ) {
                                Spacer(Modifier.height(12.dp))
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(24.dp)
                                        .background(
                                            color = if (darkTheme) MaterialTheme.colorScheme.surfaceVariant else MaterialTheme.colorScheme.primary,
                                            shape = RoundedCornerShape(16.dp)
                                        )
                                        .padding(16.dp)
                                ) {
                                    Column {
                                        Icon(
                                            Icons.Default.AccountCircle,
                                            null,
                                            modifier = Modifier.size(48.dp),
                                            tint = if (darkTheme) MaterialTheme.colorScheme.onSurfaceVariant else Color.White
                                        )
                                        Spacer(Modifier.height(12.dp))
                                        
                                        Text(
                                            text = if (userEmail != null) userEmail!! else "Invitado",
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = if (darkTheme) MaterialTheme.colorScheme.onSurfaceVariant else Color.White
                                        )
                                        
                                        if (userEmail != null) {
                                            val accessState = AppUsageManager.instance.checkAccess()
                                            val verificationStatus by AppUsageManager.instance.verificationStatus.collectAsState()
                                            val isPremium = accessState == AccessState.PREMIUM_ACTIVE
                                            
                                            val statusLabel = when {
                                                isPremium -> "Suscripción Premium"
                                                verificationStatus == VerificationStatus.GRACE_PERIOD -> "Verificar Cuenta"
                                                else -> null
                                            }

                                            if (statusLabel != null) {
                                                val badgeColor = when {
                                                    isPremium -> Color(0xFFFFD700)
                                                    verificationStatus == VerificationStatus.VERIFIED -> Color(0xFF4CAF50) // Verde para verificado
                                                    else -> Color.White.copy(alpha = 0.2f)
                                                }

                                                val badgeTextColor = if (isPremium || verificationStatus == VerificationStatus.VERIFIED) Color.Black else Color.White
                                                
                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    Surface(
                                                        color = badgeColor,
                                                        shape = RoundedCornerShape(8.dp),
                                                        modifier = Modifier.padding(top = 8.dp)
                                                    ) {
                                                        Row(
                                                            verticalAlignment = Alignment.CenterVertically,
                                                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                                                        ) {
                                                            if (isPremium) {
                                                                Icon(
                                                                    Icons.Default.WorkspacePremium,
                                                                    null,
                                                                    modifier = Modifier.size(14.dp),
                                                                    tint = Color.Black
                                                                )
                                                                Spacer(Modifier.width(4.dp))
                                                            } else if (verificationStatus == VerificationStatus.VERIFIED) {
                                                                Icon(
                                                                    Icons.Default.CheckCircle,
                                                                    null,
                                                                    modifier = Modifier.size(14.dp),
                                                                    tint = Color.Black
                                                                )
                                                                Spacer(Modifier.width(4.dp))
                                                            }
                                                            Text(
                                                                text = statusLabel,
                                                                style = MaterialTheme.typography.labelSmall,
                                                                fontWeight = FontWeight.Bold,
                                                                color = badgeTextColor
                                                            )
                                                        }
                                                    }
                                                    
                                                    Spacer(Modifier.width(8.dp))
                                                    
                                                    // Botón de Sincronización Manual
                                                    IconButton(
                                                        onClick = {
                                                            scope.launch {
                                                                FirebaseProxy.reloadUser() // Refrescar estado de verificación de Firebase
                                                                val uid = FirebaseProxy.getCurrentUserId()
                                                                if (uid != null) {
                                                                    PremiumManager.loadUserProfile(uid, forceRefresh = true)
                                                                }
                                                            }
                                                        },
                                                        modifier = Modifier.padding(top = 8.dp).size(24.dp)
                                                    ) {
                                                        Icon(
                                                            Icons.Default.Refresh, 
                                                            "Sincronizar", 
                                                            tint = Color.White.copy(alpha = 0.7f),
                                                            modifier = Modifier.size(16.dp)
                                                        )
                                                    }
                                                }
                                            }

                                            if (isPremium) {
                                                Text(
                                                    text = "¡Ya eres Premium!",
                                                    style = MaterialTheme.typography.labelSmall,
                                                    color = Color(0xFFFFD700).copy(alpha = 0.9f),
                                                    fontWeight = FontWeight.Bold,
                                                    modifier = Modifier.padding(top = 8.dp)
                                                )
                                            }
                                        }
                                    }
                                }

                                // --- Nueva Sección de Calculadoras Expandibles ---
                                HorizontalDivider(Modifier.padding(vertical = 8.dp))
                                
                                val calculadoraGroups = remember {
                                    listOf(
                                        CalculadoraGroup("Cálculos Judiciales", listOf(
                                            CalculadoraItem("Honorarios Profesionales", Routes.CALCULADORA_HONORARIOS, Icons.Default.Calculate),
                                            CalculadoraItem("Planilla de Mediación", Routes.PLANILLA_MEDIACION, Icons.Default.Handshake),
                                            CalculadoraItem("Sellados Judiciales", Routes.CODIGOS_SELLADOS, Icons.Default.AccountBalance)
                                        )),
                                        CalculadoraGroup("Plazos y Vencimientos", listOf(
                                            CalculadoraItem("Contador de Días", Routes.CALCULADORA_DIAS, Icons.Default.CalendarMonth),
                                            CalculadoraItem("Caducidad de Instancia", Routes.CALCULADORA_VENCIMIENTOS, Icons.Default.Alarm)
                                        )),
                                        CalculadoraGroup("Cálculos Económicos", listOf(
                                            CalculadoraItem("Intereses y Tasas", Routes.CALCULADORA_ECONOMICA, Icons.Default.Calculate),
                                            CalculadoraItem("Liquidador de Deuda", "liquidador_deuda", Icons.Default.Payments),
                                            CalculadoraItem("Incapacidad Física", Routes.CALCULADORA_INCAPACIDAD, Icons.Default.HealthAndSafety)
                                        )),
                                        CalculadoraGroup("Tasas y Sellados", listOf(
                                            CalculadoraItem("Sellados Judiciales", Routes.CODIGOS_SELLADOS, Icons.Default.AccountBalance),
                                            CalculadoraItem("Tasa de Justicia", Routes.TASA_JUSTICIA, Icons.Default.Payments),
                                        )),
                                        CalculadoraGroup("Índices y Valores", listOf(
                                            CalculadoraItem("Valor JUS", Routes.VALOR_JUS, Icons.AutoMirrored.Filled.TrendingUp),
                                            CalculadoraItem("Índice CAC", Routes.INDICE_CAC, Icons.Default.Construction),
                                            CalculadoraItem("IPC / Inflación", Routes.IPC_INFLACION, Icons.Default.Timeline),
                                        )),
                                        CalculadoraGroup("Documentos", listOf(
                                            CalculadoraItem("Modelos de Escritos", Routes.DOCUMENTOS, Icons.Default.Description),
                                            CalculadoraItem("Modelos de Alquiler", Routes.MODELOS_ALQUILER, Icons.Default.HomeWork),
                                            CalculadoraItem("Generador Contratos IA", Routes.GENERADOR_CONTRATO, Icons.Default.AutoAwesome),
                                        )),
                                        CalculadoraGroup("+ INFO", listOf(
                                            CalculadoraItem("Información y Ayuda", Routes.INFO_SCREEN, Icons.Default.Info)
                                        ))
                                    )
                                }

                                calculadoraGroups.forEach { group ->
                                    CalculadoraSection(
                                        group = group,
                                        currentRoute = currentRoute,
                                        navController = navController,
                                        onShowPremiumDialog = { showPremiumDialog = true }
                                    ) {
                                        scope.launch { drawerState.close() }
                                    }
                                }

                                HorizontalDivider(Modifier.padding(vertical = 8.dp))
                                
                                NavigationDrawerItem(
                                    label = { Text("Inicio") },
                                    selected = currentRoute == Routes.INICIO,
                                    onClick = {
                                        scope.launch { drawerState.close() }
                                        navController.navigate(Routes.INICIO) {
                                            popUpTo(Routes.INICIO) { inclusive = true }
                                        }
                                    },
                                    icon = { Icon(Icons.Default.Balance, null) },
                                    modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                )

                                // Enlaces de Plataforma (Discretos en el menú)
                                val platformName = getPlatform().name.lowercase()
                                val isWeb = platformName.contains("js") || platformName.contains("web")
                                val isAndroid = platformName.contains("android")

                                if (isAndroid) {
                                    NavigationDrawerItem(
                                        label = { Text("Visitar sitio Web") },
                                        selected = false,
                                        onClick = {
                                            scope.launch { drawerState.close() }
                                            openUrl("https://www.juridicorosario.com")
                                        },
                                        icon = { Icon(Icons.Default.Web, null) },
                                        modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                    )
                                } else if (isWeb) {
                                    NavigationDrawerItem(
                                        label = { Text("Descargar App Android") },
                                        selected = false,
                                        onClick = {
                                            scope.launch { drawerState.close() }
                                            openUrl("https://play.google.com/store/apps/details?id=com.rosario.juridico&pcampaignid=web_share")
                                        },
                                        icon = { Icon(Icons.Default.Shop, null) },
                                        modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                    )
                                }

                                if (userEmail != null) {
                                    val accessState = AppUsageManager.instance.checkAccess()
                                    if (accessState != AccessState.PREMIUM_ACTIVE) {
                                        NavigationDrawerItem(
                                            label = { Text("Activar Premium") },
                                            selected = currentRoute == Routes.PAYWALL,
                                            onClick = {
                                                scope.launch { drawerState.close() }
                                                navController.navigate(Routes.PAYWALL)
                                            },
                                            icon = { Icon(Icons.Default.Star, null, tint = Color(0xFFFFD700)) },
                                            modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                        )
                                        
                                        NavigationDrawerItem(
                                            label = { Text("Canjear Código") },
                                            selected = false,
                                            onClick = {
                                                scope.launch { 
                                                    drawerState.close()
                                                    val isLegitimate = FirebaseProxy.isLoggedIn() && 
                                                                       !FirebaseProxy.isAnonymous() && 
                                                                       !userEmail.isNullOrBlank()
                                                    if (isLegitimate) {
                                                        showPromoDialog = true
                                                    } else {
                                                        navController.navigate(Routes.login())
                                                    }
                                                }
                                            },
                                            icon = { Icon(Icons.Default.Payments, null, tint = MaterialTheme.colorScheme.primary) },
                                            modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                        )
                                    }

                                    NavigationDrawerItem(
                                        label = { Text("Cerrar Sesión") },
                                        selected = false,
                                        onClick = {
                                            scope.launch { drawerState.close() }
                                            showLogoutDialog = true
                                        },
                                        icon = { Icon(Icons.AutoMirrored.Filled.Logout, null, tint = MaterialTheme.colorScheme.error) },
                                        modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                    )
                                } else {
                                    NavigationDrawerItem(
                                        label = { Text("Iniciar Sesión") },
                                        selected = currentRoute == Routes.LOGIN,
                                        onClick = {
                                            scope.launch { drawerState.close() }
                                            navController.navigate(Routes.LOGIN)
                                        },
                                        icon = { Icon(Icons.AutoMirrored.Filled.Login, null, tint = MaterialTheme.colorScheme.primary) },
                                        modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                    )
                                }

                                NavigationDrawerItem(
                                    label = { Text("Compartir la App") },
                                    selected = false,
                                    onClick = {
                                        scope.launch {
                                            drawerState.close()
                                            compartirTexto("Descarga Jurídico Rosario, la mejor herramienta para abogados: https://play.google.com/store/apps/details?id=com.rosario.juridico")
                                        }
                                    },
                                    icon = { Icon(Icons.Default.Share, null) },
                                    modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                                )

                                Spacer(Modifier.height(24.dp))
                                
                                Text(
                                    "Jurídico Rosario v2.0",
                                    modifier = Modifier.padding(16.dp).align(Alignment.CenterHorizontally),
                                    style = MaterialTheme.typography.labelSmall,
                                    color = Color.Gray
                                )
                            }
                        }
                    }
                    }
                ) {
                    Surface(
                        modifier = Modifier.fillMaxSize(),
                        color    = MaterialTheme.colorScheme.background
                    ) {
                        Scaffold(
                            containerColor = MaterialTheme.colorScheme.background,
                            topBar = {
                                if (mostrarTopBar) {
                                    CenterAlignedTopAppBar(
                                        windowInsets = WindowInsets.statusBars,
                                        title = {
                                            Text(
                                                text       = titleForRoute(currentRoute),
                                                style      = MaterialTheme.typography.titleMedium,
                                                fontWeight = FontWeight.Bold
                                            )
                                        },
                                        navigationIcon = {
                                            Row {
                                                IconButton(onClick = { scope.launch { drawerState.open() } }) {
                                                    Icon(Icons.Default.Menu, "Menú", tint = if (darkTheme) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onPrimary)
                                                }
                                                if (navController.previousBackStackEntry != null) {
                                                    IconButton(onClick = { navController.popBackStack() }) {
                                                        Icon(
                                                            imageVector        = Icons.AutoMirrored.Filled.ArrowBack,
                                                            contentDescription = "Atrás",
                                                            tint               = if (darkTheme) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onPrimary
                                                        )
                                                    }
                                                }
                                            }
                                        },
                                        actions = {
                                            if (userEmail != null) {
                                                val accessState = AppUsageManager.instance.checkAccess()
                                                if (accessState != AccessState.PREMIUM_ACTIVE) {
                                                    TextButton(
                                                        onClick = { navController.navigate(Routes.PAYWALL) },
                                                        contentPadding = PaddingValues(horizontal = 8.dp)
                                                    ) {
                                                        Column(horizontalAlignment = Alignment.End) {
                                                            Text(
                                                                "ACTIVAR PREMIUM",
                                                                style = MaterialTheme.typography.labelSmall,
                                                                fontWeight = FontWeight.ExtraBold,
                                                                color = if (darkTheme) Color(0xFFFFD700) else Color.White
                                                            )
                                                        }
                                                    }
                                                } else {
                                                    Icon(
                                                        imageVector = Icons.Default.WorkspacePremium,
                                                        contentDescription = "Premium",
                                                        tint = Color(0xFFFFD700),
                                                        modifier = Modifier.padding(end = 8.dp).size(20.dp)
                                                    )
                                                }
                                                
                                                IconButton(onClick = { showLogoutDialog = true }) {
                                                    Icon(
                                                        imageVector = Icons.AutoMirrored.Filled.Logout,
                                                        contentDescription = "Cerrar Sesión",
                                                        tint = if (darkTheme) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onPrimary,
                                                        modifier = Modifier.size(20.dp)
                                                    )
                                                }
                                            }
                                        },
                                        colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                            containerColor    = if (darkTheme) MaterialTheme.colorScheme.surface else MaterialTheme.colorScheme.primary,
                                            titleContentColor = if (darkTheme) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onPrimary
                                        )
                                    )
                                }
                            },
                            bottomBar = {
                                if (!updateBannerDismissed && updateStatus != UpdateStatus.IDLE) {
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .navigationBarsPadding()
                                            .padding(16.dp),
                                        contentAlignment = Alignment.BottomCenter
                                    ) {
                                        UpdateBanner(
                                            status = updateStatus,
                                            progress = downloadProgress,
                                            onDismiss = { updateBannerDismissed = true }
                                        )
                                    }
                                }
                            },
                            snackbarHost = { SnackbarHost(hostState = snackbarHostState) }
                        ) { paddingValues ->
                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(paddingValues)
                            ) {

                                
                                Box(modifier = Modifier.weight(1f)) {
                                    // Usamos una KEY basada en el authState para que el NavGraph 
                                    // se reinicie completamente cuando cambia el usuario (Google Login).
                                    key(isAuthResolved, authState) {
                                        JuridicoNavGraph(
                                            navController        = navController,
                                            startDestination     = startDestination,
                                            marketplaceViewModel = marketplaceViewModel,
                                            onExitRequested      = { exitApp() },
                                            onOpenDrawer         = { scope.launch { drawerState.open() } }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                if (showLogoutDialog) {
                    AlertDialog(
                        onDismissRequest  = { showLogoutDialog = false },
                        title             = { Text("Cerrar Sesión") },
                        text              = { Text("¿Deseas cerrar tu sesión actual?") },
                        confirmButton     = {
                            Button(
                                onClick = { 
                                    showLogoutDialog = false
                                    scope.launch {
                                        FirebaseProxy.logout()
                                        PremiumManager.clearSession()
                                        // La reactividad de authState se encargará de resetear la vista
                                    }
                                },
                                colors  = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.error,
                                    contentColor   = MaterialTheme.colorScheme.onError
                                )
                            ) { Text("Cerrar Sesión") }
                        },
                        dismissButton = {
                            TextButton(onClick = { showLogoutDialog = false }) { Text("Cancelar") }
                        }
                    )
                }

                if (showPremiumDialog) {
                    PremiumSubscriptionDialog(
                        onDismiss = { showPremiumDialog = false },
                        onContactSupport = {
                            showPremiumDialog = false
                            openWhatsApp("5493413850100") // Usando el número que vi en PantallaInicio
                        }
                    )
                }

                if (showPromoDialog) {
                    PromoCodeDialog(
                        onDismissRequest = { showPromoDialog = false },
                        onSuccess = {
                            scope.launch {
                                val uid = FirebaseProxy.getCurrentUserId()
                                if (uid != null) {
                                    PremiumManager.loadUserProfile(uid, forceRefresh = true)
                                }
                            }
                        }
                    )
                }

                if (showNovedades) {
                    NovedadesDialog(
                        onDismiss = { 
                            showNovedades = false 
                            scope.launch {
                                prefsRepository.saveLastVersionSeen(CURRENT_APP_VERSION)
                            }
                        }
                    )
                }
            }
        }
    }



@Composable
fun SplashScreenNeutro(onIconClick: () -> Unit = {}) {
    Box(
        modifier = Modifier.fillMaxSize().background(Color(0xFF1A4B8C)),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            IconButton(
                onClick = onIconClick,
                modifier = Modifier.size(100.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Balance,
                    contentDescription = null,
                    tint = Color(0xFFFFD700),
                    modifier = Modifier.size(80.dp)
                )
            }
            Spacer(Modifier.height(24.dp))
            CircularProgressIndicator(color = Color.White)
        }
    }
}

@Composable
fun UpdateBanner(status: UpdateStatus, progress: Int, onDismiss: () -> Unit) {
    if (status == UpdateStatus.IDLE) return

    AnimatedVisibility(
        visible = true,
        enter = slideInVertically(initialOffsetY = { it }),
        exit = slideOutVertically(targetOffsetY = { it })
    ) {
        Card(
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                contentColor = MaterialTheme.colorScheme.onSecondaryContainer
            ),
            elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
            modifier = Modifier.fillMaxWidth()
        ) {
            Column(
                modifier = Modifier.padding(16.dp)
            ) {
                Row(
                    verticalAlignment = Alignment.Top,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        when (status) {
                            UpdateStatus.AVAILABLE -> {
                                Text(
                                    text = "Nueva versión disponible",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = "Mejoras y correcciones",
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                            UpdateStatus.DOWNLOADING -> {
                                Text(
                                    text = "Descargando actualización...",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                Spacer(modifier = Modifier.height(8.dp))
                                LinearProgressIndicator(
                                    progress = { progress / 100f },
                                    modifier = Modifier.fillMaxWidth(),
                                    color = MaterialTheme.colorScheme.primary,
                                    trackColor = MaterialTheme.colorScheme.surfaceVariant
                                )
                                Text(
                                    text = "$progress%",
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.align(Alignment.End)
                                )
                            }
                            UpdateStatus.DOWNLOADED -> {
                                Text(
                                    text = "¡Descarga completada!",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                Text(
                                    text = "La actualización está lista para instalar.",
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }
                            else -> {}
                        }
                    }

                    IconButton(onClick = onDismiss) {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = "Cerrar"
                        )
                    }
                }

                if (status == UpdateStatus.AVAILABLE || status == UpdateStatus.DOWNLOADED) {
                    Spacer(modifier = Modifier.height(12.dp))
                    Button(
                        onClick = {
                            if (status == UpdateStatus.AVAILABLE) {
                                AppUpdateState.onUpdateRequested?.invoke()
                            } else {
                                AppUpdateState.onInstallRequested?.invoke()
                            }
                        },
                        modifier = Modifier.align(Alignment.End),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(if (status == UpdateStatus.AVAILABLE) "Actualizar" else "Reiniciar e Instalar")
                    }
                }
            }
        }
    }
}

// ── LOGICA DE ACCESO PREMIUM ──────────────────────────────────────────

enum class AccessStatus { Granted, Denied }

object AccessManager {
    suspend fun requestAccess(route: String): AccessStatus {
        // 1. Prioridad: Estado Premium Real (Sincronizado con Google Play / Mercado Pago)
        val accessState = AppUsageManager.instance.checkAccess()
        if (accessState == AccessState.PREMIUM_ACTIVE) {
            return AccessStatus.Granted
        }

        // 2. Rutas siempre gratuitas
        val freeRoutes = listOf(Routes.INICIO, Routes.GUIA_INFO, Routes.LOGIN)
        if (freeRoutes.contains(route)) {
            return AccessStatus.Granted
        }

        // 3. Validación de usos gratuitos en Firestore (5 iniciales + 1 mensual)
        val uid = FirebaseProxy.getCurrentUserId()
        
        return if (uid != null) {
            val result = FirebaseProxy.checkAndConsumeUsage(uid)
            if (result != com.rosario.juridico.platform.AccessResult.DENIED) {
                AccessStatus.Granted
            } else {
                AccessStatus.Denied
            }
        } else {
            AccessStatus.Denied
        }
    }
}

@Composable
fun PremiumSubscriptionDialog(onDismiss: () -> Unit, onContactSupport: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                "Membresía Premium Requerida",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    "Has agotado tus usos gratuitos. Con la suscripción Premium obtendrás:",
                    style = MaterialTheme.typography.bodyMedium
                )
                val benefits = listOf(
                    "• Acceso ilimitado a todas las calculadoras.",
                    "• Uso ilimitado de IA (Consulta y Generador de Contratos).",
                    "• Actualizaciones de índices en tiempo real.",
                    "• Soporte técnico prioritario."
                )
                benefits.forEach { benefit ->
                    Text(benefit, style = MaterialTheme.typography.bodySmall)
                }
            }
        },
        confirmButton = {
            Button(onClick = onContactSupport) {
                Text("Contactar Soporte")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cerrar")
            }
        }
    )
}

@Composable
fun NovedadesDialog(onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                "¡Novedades disponibles!",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    "Estas son las mejoras de la versión:",
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.primary
                )
                val novedades = listOf(
                    "• Liquidación de Indemnización grado de Incapacidad por accidentes.",
                    "• Calculadora Honorarios Mediador ley 13.151, Abogado Ley Prov. 6.767 y Ley Nac. 27.423, Mediador PROMESA.",
                    "• Calcular Intereses Moratorios por períodos."
                )
                novedades.forEach { novedad ->
                    Text(
                        novedad,
                        style = MaterialTheme.typography.bodyMedium,
                        lineHeight = 20.sp
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = onDismiss,
                shape = RoundedCornerShape(8.dp)
            ) {
                Text("Continuar")
            }
        }
    )
}

// ── HELPERS PARA EL MENÚ LATERAL ────────────────────────────────────

data class CalculadoraItem(val title: String, val route: String, val icon: ImageVector)
data class CalculadoraGroup(val title: String, val items: List<CalculadoraItem>)

@Composable
fun CalculadoraSection(
    group: CalculadoraGroup,
    currentRoute: String?,
    navController: NavController,
    onShowPremiumDialog: () -> Unit,
    onNavigate: () -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    
    Column {
        NavigationDrawerItem(
            label = { 
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(group.title, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                    Icon(
                        if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                        contentDescription = null
                    )
                }
            },
            selected = false,
            onClick = { expanded = !expanded },
            modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
        )
        
        AnimatedVisibility(visible = expanded) {
            Column(modifier = Modifier.padding(start = 16.dp)) {
                group.items.forEach { item ->
                    NavigationDrawerItem(
                        label = { Text(item.title, style = MaterialTheme.typography.bodyMedium) },
                        selected = currentRoute == item.route,
                        onClick = {
                            scope.launch {
                                when (AccessManager.requestAccess(item.route)) {
                                    AccessStatus.Granted -> {
                                        AnalyticsLogger.logEvent("select_content", mapOf(
                                            "content_type" to "calculadora",
                                            "item_id" to item.route,
                                            "item_name" to item.title
                                        ))
                                        
                                        navController.navigate(item.route) {
                                            popUpTo(Routes.INICIO) { saveState = true }
                                            launchSingleTop = true
                                            restoreState = true
                                        }
                                        onNavigate()
                                    }
                                    AccessStatus.Denied -> {
                                        AnalyticsLogger.logEvent("paywall_viewed", mapOf(
                                            "origin" to "drawer_calculadora",
                                            "route" to item.route
                                        ))
                                        onShowPremiumDialog()
                                    }
                                }
                            }
                        },
                        icon = { Icon(item.icon, contentDescription = null, modifier = Modifier.size(20.dp)) },
                        modifier = Modifier.padding(NavigationDrawerItemDefaults.ItemPadding)
                    )
                }
            }
        }
    }
}

private fun titleForRoute(route: String?): String = when {
    route == null                              -> "JuridicoRosario.com"
    route == "consulta"                        -> "Consulta IA"
    route == "calculadora_dias"                -> "Días Hábiles y Corridos"
    route == "calculadora_vencimientos"        -> "Plazo Caducidad y Vencimientos CPCC"
    route == "calculadora_honorarios"          -> "Honorarios"
    route == "calculadora_incapacidad"         -> "Incapacidad (Art. 1746)"
    route == "calculadora_economica"           -> "Calculadora Económica"
    route.startsWith("generador_contrato")     -> "Generador Alquiler Vivienda"
    route == Routes.INFO_SCREEN                 -> "Ayuda e Información"
    route == "asistente_redaccion"             -> "Redacción IA"
    route == "guia_info"                       -> "Guía de Dependencias"
    route == "documentos"                      -> "Modelos de Locación"
    route == "modelos_alquiler"                -> "Modelos de Alquiler"
    route == "codigos_sellados"                -> "Sellados Judiciales"
    route == "registro_abogado"                -> "Registro Profesional"
    route == "publicar_consulta"               -> "Nueva Consulta"
    route == "listado_consultas"               -> "Marketplace Legal"
    route.startsWith("detalle_consulta")       -> "Detalle Legal"
    route.startsWith("chat")                   -> "Chat con Abogado"
    route == "perfil_abogado"                  -> "Mi Perfil"
    route == "agenda_expedientes"              -> "Agenda Judicial"
    route == "agenda_promesa"                  -> "Agenda PROMESA"
    route == "nuevo_expediente"                -> "Nuevo Expediente"
    route.startsWith("detalle_expediente")     -> "Detalle del Expediente"
    route == "paywall"                         -> "Membresía Premium"
    else                                       -> "JuridicoRosario.com"
}
